import { formatInTokyo } from '@common/helpers/datetime-tokyo';

/**
 * カルテ入力中の一時保存 (No.173).
 *
 * The three fields of the karte popup that a doctor can lose by closing it:
 * the comment, which 処方 button was pressed, and the plan checkboxes.
 */
export interface KarteDraftForm {
  content: string | null;
  type: string | null;
  plans: any[];
}

/**
 * What the API answers with under `draft` — null when nothing is stored.
 */
export interface KarteDraft extends KarteDraftForm {
  id: number;
  user_treatment_id: number;
  updated_at: string | null;
}

/**
 * Mirrors the 3000-character limit on both CreateFeedbackRequest and
 * SaveFeedbackDraftRequest, so text that saves as a draft can never be
 * rejected later at 保存.
 */
export const KARTE_DRAFT_MAX_CONTENT = 3000;

/**
 * Reads the three draft-relevant fields out of the popup's own form object.
 * The popup keeps far more than this on `prescriptionForm` (id, created_at,
 * listPlans, ...) — none of it belongs in a draft, which is why this picks
 * rather than spreads.
 */
export const extractKarteDraftForm = (form: any): KarteDraftForm => ({
  content: form?.content ?? null,
  type: form?.type ?? null,
  plans: Array.isArray(form?.plans) ? [...form.plans] : [],
});

/**
 * Payload for POST /users/{id}/feedback/draft. `content` is sent even when
 * empty (`present` on the API side): an empty body is how a draft is cleared.
 */
export const buildKarteDraftPayload = (
  userTreatmentId: number | null | undefined,
  form: KarteDraftForm,
) => ({
  user_treatment_id: userTreatmentId,
  content: form.content ?? null,
  type: form.type ?? null,
  plans: form.plans ?? [],
});

/**
 * Merges a restored draft back onto the popup's form object.
 *
 * Returns a new object rather than mutating: the popup assigns the result to
 * `prescriptionForm`, so Vue 2 sees one reactive write instead of three, and
 * a draft that fails to load leaves the form exactly as it was.
 *
 * Only the three draft fields are taken from the draft — everything else on
 * the current form (id, created_at, listPlans) is server truth and survives.
 */
export const applyKarteDraft = (form: any, draft: KarteDraft | null): any => {
  if (!draft) {
    return form;
  }
  return {
    ...form,
    content: draft.content ?? null,
    type: draft.type ?? null,
    plans: Array.isArray(draft.plans) ? [...draft.plans] : [],
  };
};

/**
 * Label shown next to the 一時保存 button, e.g. 「一時保存: 09月22日 14:30」.
 *
 * In Japan time via formatInTokyo — the same conversion the karte header uses,
 * so the two stamps on the popup can never disagree: a machine left on another
 * timezone would otherwise report a time the doctor never made.
 *
 * Returns '' for a missing or unparseable timestamp so the template can hide
 * the line with a plain v-if.
 */
export const formatKarteDraftSavedAt = (value: string | null): string => {
  // A missing timestamp means "no draft", not "now" — so no fallthrough into
  // formatInTokyo's now-when-empty behaviour.
  if (!value) { return ''; }
  return formatInTokyo(value, 'MM月DD日 HH:mm');
};

/**
 * Identifies one request made by a karte popup: which opening of the popup
 * started it, and for which patient and treatment.
 */
export interface KarteRequestToken {
  generation: number;
  patientId: number | string | null | undefined;
  treatmentId: number | string | null | undefined;
}

/**
 * True when a response no longer belongs to what the popup is showing, and
 * must be dropped instead of applied.
 *
 * The popup is one component instance reused across patients, and its loads
 * are deliberately not awaited on open, so a slow response for patient A can
 * land after the popup has been re-opened for patient B — and paint A's draft,
 * save time or BMI onto B's karte. Two independent checks, because each misses
 * a case the other catches:
 *  - generation: the popup was opened again since the request started. This
 *    also catches re-opening for the SAME patient, where a stale response
 *    would otherwise overwrite a fresher one arriving first;
 *  - ids: the patient or treatment on screen changed while the popup stayed
 *    open, which the generation alone does not see.
 *
 * Ids are compared as strings because they reach here from both the API and
 * the store, and one side may hold `1` where the other holds `'1'`.
 */
export const isStaleKarteRequest = (
  token: KarteRequestToken,
  currentGeneration: number,
  current: { patientId: any; treatmentId: any },
): boolean => {
  if (token.generation !== currentGeneration) {
    return true;
  }
  const same = (a: any, b: any) => String(a ?? '') === String(b ?? '');
  return !same(token.patientId, current.patientId) || !same(token.treatmentId, current.treatmentId);
};
